import React from 'react';
import { ForwardRef, typeOf } from 'react-is';
import { FormProvider as RcFormProvider } from 'rc-field-form';
import useMemo from 'rc-util/lib/hooks/useMemo';
import { RenderEmptyHandler } from './renderEmpty';
import { ConfigConsumer, ConfigContext, CSPConfig, DirectionType, ConfigConsumerProps } from './context';
import { AnimationContextProvider, defaultAnimationConfig } from './AnimationContext';
import { SizeContextProvider } from './SizeContext';
export { ConfigContext, ConfigConsumer };
import { registerTheme } from './cssVariables';
import LocaleReceiver from 'antd/es/locale-provider/LocaleReceiver';
import IconContext from '@ant-design/icons/es/components/Context';
import LocaleProvider, { ANT_MARK } from 'antd/es/locale-provider';
import defaultLocale from 'antd/es/locale/en_US';
import 'moment/dist/locale/zh-cn';
export const defaultPrefixCls = 'b-design';
export const defaultIconPrefixCls = 'b-design-icon';
export { defaultAnimationConfig };
let globalPrefixCls;
let globalIconPrefixCls;
let globalAnimationConfig;
function getGlobalPrefixCls() {
    return globalPrefixCls || defaultPrefixCls;
}
function getGlobalIconPrefixCls() {
    return globalIconPrefixCls || defaultIconPrefixCls;
}
function getGlobalAnimation() {
    return globalAnimationConfig || defaultAnimationConfig;
}
const setGlobalConfig = ({ prefixCls, iconPrefixCls, theme, animation }) => {
    if (prefixCls !== undefined) {
        globalPrefixCls = prefixCls;
    }
    if (iconPrefixCls !== undefined) {
        globalIconPrefixCls = iconPrefixCls;
    }
    if (theme) {
        registerTheme(getGlobalPrefixCls(), theme);
    }
    if (animation) {
        globalAnimationConfig = animation;
    }
};
export const globalConfig = () => ({
    getPrefixCls: (suffixCls, customizePrefixCls) => {
        if (customizePrefixCls)
            return customizePrefixCls;
        return suffixCls ? `${getGlobalPrefixCls()}-${suffixCls}` : getGlobalPrefixCls();
    },
    getIconPrefixCls: getGlobalIconPrefixCls,
    getRootPrefixCls: (rootPrefixCls, customizePrefixCls) => {
        // Customize rootPrefixCls is first priority
        if (rootPrefixCls) {
            return rootPrefixCls;
        }
        // If Global prefixCls provided, use this
        if (globalPrefixCls) {
            return globalPrefixCls;
        }
        // [Legacy] If customize prefixCls provided, we cut it to get the prefixCls
        if (customizePrefixCls && customizePrefixCls.includes('-')) {
            return customizePrefixCls.replace(/^(.*)-[^-]*$/, '$1');
        }
        // Fallback to default prefixCls
        return getGlobalPrefixCls();
    },
    getAnimation: getGlobalAnimation
});
const ProviderChildren = (props) => {
    const { children, csp, autoInsertSpaceInButton, form, locale, componentSize, direction, space, virtual, dropdownMatchSelectWidth, legacyLocale, parentContext, iconPrefixCls, animation } = props;
    const getPrefixCls = React.useCallback((suffixCls, customizePrefixCls) => {
        const { prefixCls } = props;
        if (customizePrefixCls)
            return customizePrefixCls;
        const mergedPrefixCls = prefixCls || parentContext.getPrefixCls('');
        return suffixCls ? `${mergedPrefixCls}-${suffixCls}` : mergedPrefixCls;
    }, [parentContext.getPrefixCls, props.prefixCls]);
    const config = {
        ...parentContext,
        csp,
        autoInsertSpaceInButton,
        locale: locale || legacyLocale,
        direction,
        space,
        virtual,
        dropdownMatchSelectWidth,
        getPrefixCls,
        animation
    };
    // Pass the props used by `useContext` directly with child component.
    // These props should merged into `config`.
    // PASSED_PROPS.forEach((propName) => {
    // 	const propValue: any = props[propName];
    // 	if (propValue) {
    // 		(config as any)[propName] = propValue;
    // 	}
    // });
    // https://github.com/ant-design/ant-design/issues/27617
    const memoedConfig = useMemo(() => config, config, (prevConfig, currentConfig) => {
        const prevKeys = Object.keys(prevConfig);
        const currentKeys = Object.keys(currentConfig);
        return (prevKeys.length !== currentKeys.length || prevKeys.some((key) => prevConfig[key] !== currentConfig[key]));
    });
    const memoIconContextValue = React.useMemo(() => ({ prefixCls: iconPrefixCls, csp }), [iconPrefixCls, csp]);
    let childNode = children;
    // Additional Form provider
    let validateMessages = {};
    if (locale) {
        validateMessages = locale.Form?.defaultValidateMessages || defaultLocale.Form?.defaultValidateMessages || {};
    }
    if (form && form.validateMessages) {
        validateMessages = { ...validateMessages, ...form.validateMessages };
    }
    if (Object.keys(validateMessages).length > 0) {
        childNode = React.createElement(RcFormProvider, { validateMessages: validateMessages }, children);
    }
    if (locale) {
        childNode = (React.createElement(LocaleProvider, { locale: locale, _ANT_MARK__: ANT_MARK }, childNode));
    }
    if (iconPrefixCls || csp) {
        childNode = React.createElement(IconContext.Provider, { value: memoIconContextValue }, childNode);
    }
    if (componentSize) {
        childNode = React.createElement(SizeContextProvider, { size: componentSize }, childNode);
    }
    if (animation) {
        childNode = React.createElement(AnimationContextProvider, { ...animation }, childNode);
    }
    // @ts-ignore
    return React.createElement(ConfigContext.Provider, { value: memoedConfig }, childNode);
};
const ConfigProvider = 
// : React.FC<ConfigProviderProps> & {
// 	ConfigContext: typeof ConfigContext;
// 	SizeContext: typeof SizeContext;
// 	config: typeof setGlobalConfig;
// }
(props) => {
    React.useEffect(() => {
        if (props.direction) {
            // message.config({
            // 	rtl: props.direction === 'rtl'
            // });
            // notification.config({
            // 	rtl: props.direction === 'rtl'
            // });
        }
    }, [props.direction]);
    return (React.createElement(LocaleReceiver, null, (_, __, legacyLocale) => (React.createElement(ConfigConsumer, null, (context) => (React.createElement(ProviderChildren, { parentContext: context, legacyLocale: legacyLocale, ...props }))))));
};
ConfigProvider.config = setGlobalConfig;
ConfigProvider.defaultProps = {
    prefixCls: defaultPrefixCls,
    iconPrefixCls: defaultIconPrefixCls,
    animation: {
        glassmorphism: true,
        breathing: true
    },
    autoInsertSpaceInButton: false
};
export function withConfigProvider(Component) {
    const isClassComponent = (object) => !!Component?.prototype?.render;
    const isForwardRef = (object) => typeOf(object) === ForwardRef;
    let ComponentWithProvider = (props) => {
        return (React.createElement(ConfigProvider, null,
            React.createElement(Component, { ...props })));
    };
    if (isForwardRef(Component)) {
        // eslint-disable-next-line react/display-name
        ComponentWithProvider = React.forwardRef((props, ref) => {
            return (React.createElement(ConfigProvider, null,
                React.createElement(Component, { ...props, ref: ref })));
        });
    }
    else if (isClassComponent(Component)) {
        class ComponentWithProvider extends React.Component {
            render() {
                // @ts-ignore
                return React.createElement(Component, { ...this.props });
            }
        }
        const ownProperties = Object.getOwnPropertyNames(Component).filter((properyty) => !['$$typeof', 'render', 'displayName', 'prototype', 'arguments', 'caller'].includes(properyty));
        ownProperties.forEach((property) => {
            // A robust way of ComponentWithProvider[property] = Component[property];
            Object.defineProperty(ComponentWithProvider, property, {
                value: Component[property],
                writable: true,
                configurable: true
            });
        });
        // @ts-ignore
        return ComponentWithProvider;
    }
    // Object properties like 'arguments', 'caller' is non-configurable in regular functions.
    const ownProperties = Object.getOwnPropertyNames(Component).filter((properyty) => !['$$typeof', 'render', 'prototype', 'arguments', 'caller'].includes(properyty));
    ownProperties.forEach((property) => {
        // A robust way of ComponentWithProvider[property] = Component[property];
        Object.defineProperty(ComponentWithProvider, property, {
            value: Component[property],
            writable: true,
            configurable: true
        });
    });
    // @ts-ignore
    return ComponentWithProvider;
}
/** @private internal Usage. do not use in your production */
// setConfig
ConfigProvider.config({
    prefixCls: defaultPrefixCls,
    iconPrefixCls: defaultIconPrefixCls,
    animation: {
        glassmorphism: true,
        breathing: true
    }
});
// console.log(getGlobalPrefixCls(), '====ConfigProvider');
export default ConfigProvider;
